import DuoSignUpLayout from '@/components/v2/DuoSignUpLayout'
import DuoTextField from '@/components/v2/DuoTextField'
import AlreadySignedUpDialog from '@app-studio-web/ui/components/AlreadySignedUpDialog'
import { trackCompleteRegistrationEvent } from '@app-studio-web/ui/helpers/facebookTracking'
import {
  getPasswordFromSessionStorage,
  removePasswordFromSessionStorage,
} from '@app-studio-web/ui/helpers/sessionStorage'
import { signUp } from '@app-studio-web/ui/helpers/supabase'
import { validateEmail } from '@app-studio-web/ui/helpers/validations'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

/**
 * The email half of Duolingo's "Create your profile" form, followed by the
 * double-check step the shared `EmailPage` has. Everything behind the UI is the
 * shared page's: the same validation, the same session-storage handoff from the
 * password step, the same supabase `signUp`, the same already-registered dialog
 * and the same hop to /sign-up/preparing after tracking the registration.
 */
export default function EmailPage() {
  const [email, setEmail] = useState('')
  const [emailValid, setEmailValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState('')
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmailValid = validateEmail(email)

  const doSignUp = async () => {
    setIsSubmitting(true)

    const { error } = await signUp({ email, password })

    if (error) {
      if (error.message === 'User already registered') {
        setOpenAlreadySignedUpDialog(true)
      } else {
        console.error(error)
      }

      setIsSubmitting(false)
      setShowConfirm(false)

      return
    }

    removePasswordFromSessionStorage()

    await trackCompleteRegistrationEvent()

    navigate('/sign-up/preparing')
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    if (!isEmailValid) {
      setEmailValid(false)
      return
    }

    setEmailValid(true)
    setShowConfirm(true)
  }

  useEffect(() => {
    const passwordFromSessionStorage = getPasswordFromSessionStorage()

    if (passwordFromSessionStorage) {
      setPassword(passwordFromSessionStorage)
    } else {
      navigate('/sign-up/password')
    }
  }, [navigate])

  if (showConfirm) {
    return (
      <div className="min-h-dvh bg-white">
        <main className="duo-container flex min-h-dvh flex-col pt-6 pb-8">
          <div className="flex grow flex-col justify-center gap-6 text-center">
            <div className="space-y-2">
              <h1 className="text-duo-ink text-[26px] leading-tight font-extrabold text-balance">
                {t('components.emailPreSignupConfirm.title')}
              </h1>

              <p className="text-duo-muted text-[15px] leading-relaxed text-balance">
                {t('components.emailPreSignupConfirm.subtitle')}
              </p>
            </div>

            <p
              className="text-duo-ink text-xl font-extrabold break-all"
              data-csr-mask
            >
              {email}
            </p>

            <div className="space-y-3">
              <button
                className="duo-button-primary"
                disabled={isSubmitting}
                onClick={doSignUp}
                type="button"
              >
                {t('components.emailPreSignupConfirm.confirm')}
              </button>

              <button
                className="duo-button-secondary"
                disabled={isSubmitting}
                onClick={() => setShowConfirm(false)}
                type="button"
              >
                {t('components.emailPreSignupConfirm.edit')}
              </button>
            </div>
          </div>
        </main>

        <AlreadySignedUpDialog
          open={openAlreadySignedUpDialog}
          onClose={() => setOpenAlreadySignedUpDialog(false)}
        />
      </div>
    )
  }

  return (
    <>
      <DuoSignUpLayout
        fineprint={
          <p
            className={
              emailValid
                ? 'text-duo-muted text-center text-xs leading-relaxed'
                : 'text-duo-red text-center text-xs leading-relaxed'
            }
          >
            {emailValid ? (
              <Trans
                components={{
                  privacyPolicyLink: (
                    <Link
                      className="text-duo-ink font-bold"
                      target="_blank"
                      to="https://app-studio.ai/privacy"
                    />
                  ),
                  termsAndConditionsLink: (
                    <Link
                      className="text-duo-ink font-bold"
                      target="_blank"
                      to="https://app-studio.ai/terms"
                    />
                  ),
                }}
                i18nKey="pages.signUp.email.text"
              />
            ) : (
              t('pages.signUp.email.emailNotValid')
            )}
          </p>
        }
      >
        <form className="space-y-4" noValidate onSubmit={handleSubmit}>
          <DuoTextField
            autoComplete="email"
            dataTestId="textfield-email"
            invalid={!emailValid}
            name="email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('shared.email')}
            type="email"
            value={email}
          />

          <button
            className="duo-button-primary"
            data-testid="button-continue-credentials"
            disabled={email.length < 1 || isSubmitting}
            type="submit"
          >
            {t('pages.signUp.v2.form.createAccount')}
          </button>
        </form>
      </DuoSignUpLayout>

      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
